import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AccountTabs, Transactions, Button } from "../../../components";
import RepayDates from "./RepayDates";
import { selectAccessToken } from "../../../slices/staffSlice";
import { authStaff } from "../../../utils/constants";
import { accessFileRequest } from "../../../Requests/getRequests";
import { show } from "../../../slices/toastSlice";

function Index({ loan }) {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const [processing, setProcessing] = useState(false);
    const [exporting, setExporting] = useState(false);

    const exportDates = async () => {
        setExporting(true);
        let res = await accessFileRequest(`/reports/staff/exportRepaymentDates`, authStaff(accessToken), { _id: loan?._id });
        setExporting(false);

        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: `${res?.err}` }));
            return;
        }

        const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', `repayment-dates-${loan?.loanId}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const downloadReport = async () => {
        // loan.url is the statement path generated server-side when the loan is
        // fetched. It is null when the report service could not produce one (and
        // may be an { err } object from an older backend), so resolve it safely
        // instead of dereferencing it — otherwise this throws before the request
        // is ever sent and leaves the button spinning.
        const name = typeof loan?.url === "string" ? loan.url.split("/")[1] : null;
        if (!name) {
            dispatch(show({ type: "error", state: true, message: "Statement is not available for this loan yet. Please refresh and try again." }));
            return;
        }

        setProcessing(true);
        try {
            let res = await accessFileRequest(`/reports/staff/getReportFile`, authStaff(accessToken), { name });
            if (!res || res.err || !res.data) {
                dispatch(show({ type: "error", state: true, message: res?.err || "Could not download the statement." }));
                return;
            }

            const fileUrl = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = fileUrl;
            const disposition = res.headers?.['content-disposition'];
            const fileName = disposition ? disposition.split('filename=')[1].replace(/["']/g, '') : name;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(fileUrl);
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="space-y-5 py-4">
            <AccountTabs loan={loan} />

            {loan?.state === "active" && loan?.standingOrderDates?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Standing Order Payment Dates <span className="text-xs font-normal text-gray-400 dark:text-gray-500 ml-1">(+5)</span></h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        <RepayDates dates={loan.standingOrderDates} />
                    </div>
                </div>
            )}

            {loan?.state === "active" && loan?.paymentDates?.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Payment Dates</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        <RepayDates dates={loan.paymentDates} />
                    </div>
                </div>
            )}

            {loan?.state === "active" && (
                <div>
                    <Button
                        btnText="Export Loan Repayment Dates"
                        btnClasses="form-button w-auto px-6"
                        btnType="button"
                        btnFunction={exportDates}
                        processing={exporting}
                    />
                </div>
            )}

            {(loan?.state === "active" || loan?.state === "completed") && (
                <>
                <Transactions loan={loan} />
                <div>
                    <Button
                        btnText="Generate Statement"
                        btnClasses="form-button w-auto px-6"
                        btnType="button"
                        btnFunction={downloadReport}
                        processing={processing}
                    />
                </div>
                </>
            )}
        </div>
    );
}

export default Index;
